import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Chip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Edit, Delete, Visibility } from "@mui/icons-material";
import { partnerService, Partner } from "../../apis/partners";
import { offerTypeService, OfferType } from "../../apis/offerTypes";
import CreatePartnerModal from "./CreatePartnerModal";
import EditPartnerModal from "./EditPartnerModal";
import PartnerDetailsModal from "./PartnerDetailsModal";

const PartnerList: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [offerTypes, setOfferTypes] = useState<OfferType[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });
  const [earningsMap, setEarningsMap] = useState<Record<string, { total_earnings: number; paid_amount: number; pending_amount: number; total_rides: number }> | {}>({});

  useEffect(() => {
    fetchPartners();
    fetchOfferTypes();
  }, [page, rowsPerPage]);

  const fetchOfferTypes = async () => {
    try {
      const response = await offerTypeService.getActiveOfferTypes();
      if (response.status === "success" || response.status === true) {
        setOfferTypes(response.data.offer_types || []);
      }
    } catch (error) {
      console.error("Failed to fetch offer types:", error);
    }
  };

  const getOfferTypeName = (offerTypeId: string) => {
    const offerType = offerTypes.find((ot) => ot._id === offerTypeId);
    return offerType ? offerType.name : offerTypeId;
  };

  const fetchPartners = async () => {
    setLoading(true);
    try {
      // Fetch partners and earnings in parallel
      const [response, earnings] = await Promise.all([
        partnerService.getPartners(page + 1, rowsPerPage),
        partnerService.getAllPartnersEarningsSummary().catch(() => ({} as Record<string, { total_earnings: number; paid_amount: number; pending_amount: number; total_rides: number }>)) // Don't fail if earnings fetch fails
      ]);
      
      console.log("Partners API Response:", response);
      // Handle both wrapped response (status/data) and direct response
      let partnersList: Partner[] = [];
      if (response.status === "success" || response.status === true) {
        // Wrapped response format
        partnersList = response.data?.partners || [];
        setTotal(response.data?.total || 0);
      } else if (response.partners) {
        // Direct response format (auth service returns data directly)
        console.log("Using direct response format");
        partnersList = response.partners || [];
        setTotal(response.total || 0);
      } else {
        console.warn("Unexpected response format:", response);
      }
      
      // Update earnings map - ensure it's always a Record
      const earningsRecord: Record<string, { total_earnings: number; paid_amount: number; pending_amount: number; total_rides: number }> = 
        (earnings && typeof earnings === 'object' && !Array.isArray(earnings)) 
          ? earnings as Record<string, { total_earnings: number; paid_amount: number; pending_amount: number; total_rides: number }>
          : {};
      setEarningsMap(earningsRecord);
      
      // Enrich partners with earnings data
      // Prioritize totalEarnings from partner API, use earnings summary for pending/paid amounts
      const enrichedPartners = partnersList.map(partner => {
        const partnerEarnings = earningsRecord[partner._id];
        return {
          ...partner,
          // Use totalEarnings from partner API if available, otherwise fall back to earnings summary
          totalEarnings: partner.totalEarnings ?? partnerEarnings?.total_earnings ?? 0,
          // Use earnings summary for pending and paid amounts (not in partner API response)
          pendingAmount: partnerEarnings?.pending_amount ?? partner.pendingAmount ?? 0,
          paidAmount: partnerEarnings?.paid_amount ?? partner.paidAmount ?? 0,
        };
      });
      
      setPartners(enrichedPartners);
    } catch (error: any) {
      console.error("Error fetching partners:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch partners",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePartner = () => {
    setSelectedPartner(null);
    setOpenCreateModal(true);
  };

  const handleEditPartner = (partner: Partner) => {
    setSelectedPartner(partner);
    setOpenEditModal(true);
  };

  const handleViewDetails = (partner: Partner) => {
    setSelectedPartner(partner);
    setOpenDetailsModal(true);
  };

  const handleDeletePartner = (partner: Partner) => {
    setSelectedPartner(partner);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedPartner) return;
    try {
      await partnerService.deletePartner(selectedPartner._id);
      setSnackbar({
        open: true,
        message: "Partner deleted successfully",
        severity: "success",
      });
      setOpenDeleteDialog(false);
      setSelectedPartner(null);
      fetchPartners();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to delete partner",
        severity: "error",
      });
    }
  };

  const handleCloseModals = () => {
    setOpenCreateModal(false);
    setOpenEditModal(false);
    setOpenDetailsModal(false);
    setSelectedPartner(null);
    fetchPartners();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getOfferTypeColor = (offerTypeId: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const offerType = offerTypes.find((ot) => ot._id === offerTypeId);
    // Default color logic - can be enhanced
    return "primary";
  };

  const handleTriggerPayouts = async () => {
    if (!window.confirm("Are you sure you want to process partner payouts now? This will process payouts for all partners who are due.")) {
      return;
    }
    
    setLoading(true);
    try {
      const result = await partnerService.triggerPartnerPayouts();
      setSnackbar({
        open: true,
        message: `Payout process completed. Processed ${result.processed} out of ${result.total} partners.`,
        severity: "success",
      });
      // Refresh partners list to show updated earnings
      fetchPartners();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to trigger partner payouts",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5">Partners Management</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={handleTriggerPayouts}
            disabled={loading}
          >
            {loading ? "Processing..." : "Process Payouts Now"}
          </Button>
          <Button variant="contained" color="primary" onClick={handleCreatePartner}>
            Add Partner
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Partner Name</TableCell>
              <TableCell>Company Name</TableCell>
              <TableCell>Contact Details</TableCell>
              <TableCell>Offer Type</TableCell>
              <TableCell>Payment Frequency</TableCell>
              <TableCell>Cars Count</TableCell>
              <TableCell>Drivers Count</TableCell>
              <TableCell>Total Earnings</TableCell>
              <TableCell>Pending Amount</TableCell>
              <TableCell>Paid Amount</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : partners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No partners found
                </TableCell>
              </TableRow>
            ) : (
              partners.map((partner) => (
                <TableRow key={partner._id}>
                  <TableCell>{`${partner.firstName} ${partner.lastName}`}</TableCell>
                  <TableCell>{partner.companyName || "-"}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{partner.email}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {partner.phone}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getOfferTypeName(partner.offerTypeId)}
                      color={getOfferTypeColor(partner.offerTypeId)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {partner.paymentFrequency 
                      ? `${partner.paymentFrequency} week${partner.paymentFrequency > 1 ? 's' : ''}`
                      : 'N/A'}
                  </TableCell>
                  <TableCell>{partner.carsCount || 0}</TableCell>
                  <TableCell>{partner.driversCount || 0}</TableCell>
                  <TableCell>{(partner.totalEarnings || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} FCFA</TableCell>
                  <TableCell>{(partner.pendingAmount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} FCFA</TableCell>
                  <TableCell>{(partner.paidAmount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} FCFA</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleViewDetails(partner)}>
                      <Visibility />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleEditPartner(partner)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeletePartner(partner)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <CreatePartnerModal open={openCreateModal} onClose={handleCloseModals} />
      <EditPartnerModal
        open={openEditModal}
        onClose={handleCloseModals}
        partner={selectedPartner}
      />
      <PartnerDetailsModal
        open={openDetailsModal}
        onClose={handleCloseModals}
        partnerId={selectedPartner?._id}
      />

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Partner</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedPartner?.firstName} {selectedPartner?.lastName}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerList;

